import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLocale } from '../utils/locale';
import { api } from '../services/api';

const COLOR_PRESETS = [
  '#D72638', '#1E6FD9', '#2ECC71', '#8E44AD',
  '#E67E22', '#16A085', '#C0392B', '#2C3E50',
];

export default function BrandingSettings() {
  const { trainer, refreshTrainer } = useAuth();
  const { t } = useLocale();
  const [branding, setBranding] = useState({ brand_logo_url: null, brand_tagline: '', brand_color: '#D72638' });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [msg, setMsg] = useState(null);
  const fileRef = useRef();

  useEffect(() => {
    api.get('/settings/branding').then(d => {
      setBranding({
        brand_logo_url: d.brand_logo_url || null,
        brand_tagline:  d.brand_tagline  || '',
        brand_color:    d.brand_color    || '#D72638',
      });
    }).catch(() => {});
  }, []);

  async function handleLogoUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    setMsg(null);
    try {
      const fd = new FormData();
      fd.append('logo', file);
      const res = await fetch('/api/settings/branding/logo', {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('ptm_token')}` },
        body: fd,
      });
      if (!res.ok) { const b = await res.json(); throw new Error(b.message || 'Upload failed'); }
      const data = await res.json();
      setBranding(b => ({ ...b, brand_logo_url: data.brand_logo_url }));
      await refreshTrainer();
      setMsg({ type: 'success', text: 'Logo uploaded successfully' });
    } catch (err) {
      setMsg({ type: 'error', text: err.message });
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  }

  async function handleRemoveLogo() {
    if (!window.confirm('Remove your brand logo?')) return;
    try {
      await api.delete('/settings/branding/logo');
      setBranding(b => ({ ...b, brand_logo_url: null }));
      await refreshTrainer();
      setMsg({ type: 'success', text: 'Logo removed' });
    } catch (err) {
      setMsg({ type: 'error', text: err.message });
    }
  }

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    setMsg(null);
    try {
      await api.put('/settings/branding', {
        brand_tagline: branding.brand_tagline,
        brand_color:   branding.brand_color,
      });
      await refreshTrainer();
      setMsg({ type: 'success', text: 'Branding saved' });
    } catch (err) {
      setMsg({ type: 'error', text: err.message });
    } finally {
      setSaving(false);
    }
  }

  const brandColor   = branding.brand_color || '#D72638';
  const businessName = trainer?.business_name || trainer?.name || 'Your Business';

  return (
    <div>
      <div className="page-header">
        <h1>Branding</h1>
      </div>

      <div className="page-content" style={{ maxWidth: 700 }}>
        {msg && (
          <div className={msg.type === 'success' ? 'success-msg' : 'error-msg'}>
            {msg.text}
          </div>
        )}

        {/* ── Logo ── */}
        <div className="card" style={{ marginBottom: 16 }}>
          <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>Brand Logo</div>
          <div style={{ fontSize: 13, color: 'var(--color-text-muted)', marginBottom: 16 }}>
            PNG or JPG, max 5 MB. Appears on invoices and next to your business name in the sidebar.
          </div>

          {branding.brand_logo_url ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
              <div style={{ background: 'var(--color-surface-2)', borderRadius: 10, padding: '12px 20px', display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: 140, minHeight: 80 }}>
                <img src={branding.brand_logo_url} alt="Brand logo"
                  style={{ maxWidth: 160, maxHeight: 72, objectFit: 'contain' }} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <button className="btn btn-ghost"
                  style={{ width: 'auto', padding: '0 18px', fontSize: 13, minHeight: 36 }}
                  onClick={() => fileRef.current.click()} disabled={uploading}>
                  {uploading ? 'Uploading…' : 'Replace Logo'}
                </button>
                <button onClick={handleRemoveLogo}
                  style={{ background: 'none', border: 'none', color: 'var(--color-primary)', fontSize: 13, cursor: 'pointer', padding: 0, fontFamily: 'var(--font)', textAlign: 'left' }}>
                  Remove
                </button>
              </div>
            </div>
          ) : (
            <button onClick={() => fileRef.current.click()} disabled={uploading}
              style={{ width: '100%', minHeight: 100, background: 'var(--color-surface-2)', border: '2px dashed var(--color-border)', borderRadius: 10, cursor: 'pointer', color: 'var(--color-text-muted)', fontSize: 14, fontFamily: 'var(--font)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, transition: 'border-color 0.15s' }}>
              <span style={{ fontSize: 32, opacity: 0.5 }}>↑</span>
              {uploading ? 'Uploading…' : 'Click to upload logo'}
              <span style={{ fontSize: 12 }}>PNG or JPG</span>
            </button>
          )}
          <input ref={fileRef} type="file" accept=".png,.jpg,.jpeg" style={{ display: 'none' }} onChange={handleLogoUpload} />
        </div>

        <form onSubmit={handleSave}>
          {/* ── Tagline ── */}
          <div className="card" style={{ marginBottom: 16 }}>
            <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>Tagline / Mission Statement</div>
            <div style={{ fontSize: 13, color: 'var(--color-text-muted)', marginBottom: 12 }}>
              Appears below your business name on invoices.
            </div>
            <textarea className="form-textarea"
              placeholder="e.g. Transforming lives through fitness"
              value={branding.brand_tagline}
              onChange={e => setBranding(b => ({ ...b, brand_tagline: e.target.value }))}
              maxLength={120}
              style={{ minHeight: 72 }} />
            <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginTop: 4, textAlign: 'right' }}>
              {branding.brand_tagline.length}/120
            </div>
          </div>

          {/* ── Brand colour ── */}
          <div className="card" style={{ marginBottom: 16 }}>
            <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>{t('Brand Color')}</div>
            <div style={{ fontSize: 13, color: 'var(--color-text-muted)', marginBottom: 14 }}>
              {t('Used as the accent color on invoices.')}
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
              {COLOR_PRESETS.map(hex => (
                <button key={hex} type="button" onClick={() => setBranding(b => ({ ...b, brand_color: hex }))}
                  title={hex}
                  style={{ width: 34, height: 34, borderRadius: '50%', background: hex, border: branding.brand_color === hex ? '3px solid #fff' : '3px solid transparent', boxShadow: branding.brand_color === hex ? `0 0 0 2px ${hex}` : 'none', cursor: 'pointer', flexShrink: 0, transition: 'box-shadow 0.15s' }} />
              ))}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <input type="color" value={brandColor}
                onChange={e => setBranding(b => ({ ...b, brand_color: e.target.value }))}
                style={{ width: 40, height: 40, border: '1px solid var(--color-border)', background: 'none', cursor: 'pointer', padding: 2, borderRadius: 6 }} />
              <input className="form-input" value={brandColor}
                onChange={e => setBranding(b => ({ ...b, brand_color: e.target.value }))}
                placeholder="#D72638"
                style={{ maxWidth: 120, fontFamily: 'monospace', fontSize: 14 }} />
              <span style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>Custom hex</span>
            </div>
          </div>

          {/* ── Invoice preview ── */}
          <div className="card" style={{ marginBottom: 20 }}>
            <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 14 }}>Invoice Preview</div>
            <div style={{ border: '1px solid var(--color-border)', borderRadius: 10, overflow: 'hidden' }}>
              {/* Header band */}
              <div style={{ background: brandColor, padding: '20px 24px', display: 'flex', alignItems: 'center', gap: 16 }}>
                {branding.brand_logo_url
                  ? <img src={branding.brand_logo_url} alt="" style={{ height: 44, maxWidth: 110, objectFit: 'contain' }} />
                  : <div style={{ width: 44, height: 44, borderRadius: 8, background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 20, fontWeight: 800, flexShrink: 0 }}>
                      {businessName.charAt(0)}
                    </div>
                }
                <div style={{ flex: 1 }}>
                  <div style={{ color: '#fff', fontWeight: 700, fontSize: 15 }}>{businessName}</div>
                  {branding.brand_tagline && (
                    <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: 12, marginTop: 2 }}>
                      {branding.brand_tagline}
                    </div>
                  )}
                </div>
                <div style={{ color: 'rgba(255,255,255,0.9)', fontWeight: 800, fontSize: 16, letterSpacing: 2 }}>INVOICE</div>
              </div>
              {/* Body sample */}
              <div style={{ padding: '16px 24px', background: '#1a1a1a', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>Bill To</div>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>Client Name</div>
                  <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginTop: 2 }}>Personal Training · 4 sessions</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 24, fontWeight: 800, color: brandColor }}>£0.00</div>
                  <div style={{ fontSize: 12, color: '#F39C12', fontWeight: 600 }}>Pending</div>
                </div>
              </div>
            </div>
          </div>

          <button className="btn btn-primary" type="submit" disabled={saving}>
            {saving ? 'Saving…' : 'Save Branding'}
          </button>
        </form>
      </div>
    </div>
  );
}
